import { type ReactNode, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

// eslint-disable-next-line boundaries/element-types
import { isDevelopment } from "@av/build-constants";
import type { IEntityPermissions } from "@av/config/api-config/permissions/entity-permissions.interface";
import { SubmittedFieldsMapper } from "./submitted-fields-mapper";
import { FormMetaContext } from "../meta-context/FormMetaContext";

import type { AntdFieldName, CustomFormItemProps, FieldName, FormExternalError } from "../types";
import type { FieldData } from "rc-field-form/lib/interface";

function toKebabCase(value: string) {
  return value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

function getErrorContent(
  errorMessage: string,
  errorCode: string | undefined,
  testIdPrefix: string
): ReactNode {
  if (!errorCode) {
    return errorMessage;
  }

  const errorType = toKebabCase(errorCode);

  return <span data-testid={`error-${testIdPrefix}-${errorType}`}>{errorMessage}</span>;
}

export function useFieldDataMapper() {
  const { trackedFieldProps } = useContext(FormMetaContext);
  const { t } = useTranslation();

  const splitName = useCallback(
    (transformedName: string | undefined): AntdFieldName | undefined => {
      if (!transformedName) {
        return undefined;
      }

      return transformedName.split(".").map((v: string) => (isNaN(Number(v)) ? v : Number(v)));
    },
    []
  );

  const joinName = useCallback((name: AntdFieldName): FieldName => {
    return Array.isArray(name) ? name.join(".") : String(name);
  }, []);

  const mapExternalErrorsToFieldData = useCallback(
    (serverErrors: FormExternalError[]): FieldData[] => {
      const labelMissingErrors: FormExternalError[] = [];

      const fieldDataMap = serverErrors.reduce(
        (acc, serverError) => {
          const name = serverError.field;

          if (!trackedFieldProps[name]?.label) {
            labelMissingErrors.push(serverError);

            return acc;
          }

          if (!acc[name]) {
            acc[name] = {
              name: splitName(name) ?? name,
              errors: [],
              warnings: [],
              touched: true,
              validating: false,
            } satisfies FieldData;
          }

          const fieldData = acc[name];

          const errorParams = serverError.error.i18nParams ?? {};
          const hasExample = "example" in errorParams && errorParams.example != null;
          const field: ReactNode = trackedFieldProps[name].label;
          const errorMessage = t(serverError.error.i18nTemplate, {
            ...errorParams,
            field,
            ...(hasExample ? { context: "withExample" } : {}),
          });
          const error = getErrorContent(
            errorMessage,
            serverError.error.code,
            trackedFieldProps[name].testIdPrefix ?? name
          );

          if (serverError.severity === "Warning") {
            (fieldData.warnings as ReactNode[]).push(error);
          } else {
            (fieldData.errors as ReactNode[]).push(error);
          }

          return acc;
        },
        {} as Record<string, FieldData>
      );

      labelMissingErrors.forEach((err: FormExternalError) => {
        const prefix = err.field + ".";

        Object.entries(trackedFieldProps).forEach(([name, fieldProps]) => {
          if (name !== err.field && !name.startsWith(prefix)) return;

          if (!fieldDataMap[name]) {
            fieldDataMap[name] = {
              name,
              errors: [],
              warnings: [],
              touched: true,
              validating: false,
            } satisfies FieldData;
          }

          const fieldData = fieldDataMap[name];

          const errorParams = err.error.i18nParams ?? {};
          const hasExample = "example" in errorParams && errorParams.example != null;

          const field: ReactNode = fieldProps.label;
          const errorMessage = t(err.error.i18nTemplate, {
            ...errorParams,
            field,
            ...(hasExample ? { context: "withExample" } : {}),
          });
          const error = getErrorContent(
            errorMessage,
            err.error.code,
            fieldProps.testIdPrefix ?? name
          );

          if (err.severity === "Warning") {
            (fieldData.warnings as ReactNode[]).push(error);
          } else {
            (fieldData.errors as ReactNode[]).push(error);
          }
        });
      });

      return Object.values(fieldDataMap);
    },
    [trackedFieldProps, splitName, t]
  );

  const mapFieldsToSubmittedObject = useCallback(
    (model: object, permissions?: IEntityPermissions) => {
      const mapper = new SubmittedFieldsMapper(permissions);
      const submittedObject = mapper.map(model);

      return submittedObject;
    },
    []
  );

  return {
    mapExternalErrorsToFieldData,
    mapFieldsToSubmittedObject,
    trackedFieldProps,
    splitName,
    joinName,
  };
}

/**
 * We use a string field name as a first class way to show dev errors
 * When a label is a react component instead of a string its expect to see a fallback `labelString`
 * In dev environments this will throw an error
 * but in prod we will simply use the name prop
 * which has the least degredation in UX
 */
export function assertValidPropsInDev(props: CustomFormItemProps) {
  const { labelString, label } = props;

  if (!isDevelopment || !label) {
    return;
  }

  // Non number falsy value means we dont have to handle a label
  if (isNaN(label as number) && !label) {
    return;
  }

  if (typeof label !== "string" && !labelString) {
    const stringProps = Object.fromEntries(
      Object.entries(props).filter(([, value]) => typeof value === "string")
    );

    throw Error(
      `Form.Item component with non string 'label' prop requires a 'labelString' prop. Form.Item props: ${JSON.stringify(stringProps)}`
    );
  }
}

export function resolveLabelNameString(
  name: FieldName | undefined,
  label: string | ReactNode,
  labelString?: string
): FieldName | null | undefined {
  if (labelString) {
    return labelString;
  }

  // Non number falsy value means we dont have to handle a label
  if (isNaN(label as number) && !label) {
    return name;
  }

  if (typeof label === "string") {
    return label;
  }

  return name;
}
